import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getShifts, getBranches, closeShift } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CloseShiftModal({ shift, onClose, onSaved }) {
  const token = localStorage.getItem("token");
  const { t } = useTranslation("shifts");
  const { t: tCommon } = useTranslation("common");
  const [closingBalance, setClosingBalance] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (closingBalance === "" || Number(closingBalance) < 0) {
      setError(t("validation_cash_required", "Enter the actual counted cash amount"));
      return;
    }
    setLoading(true);
    try {
      await closeShift(token, shift.id, { closing_balance: Number(closingBalance), notes: notes.trim() });
      notify.success(t("notify_closed", "Shift closed successfully"));
      onSaved();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_close_failed", "Failed to close shift"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl">
        <h3 className="text-xl font-black text-gray-900 mb-1">{t("modal_title", "Close Shift")}</h3>
        <p className="text-gray-400 text-sm mb-6">
          {t("modal_subtitle", "{branch} — opened with {amount}").replace("{branch}", shift.branch?.name).replace("{amount}", money(shift.opening_balance))}
        </p>

        <div className="space-y-5">
          <FloatingInput
            label={t("field_actual_cash_label", "Actual Cash Counted")}
            type="number"
            value={closingBalance}
            onChange={(e) => { setClosingBalance(e.target.value); setError(""); }}
            error={error}
            icon={ICONS.Shifts}
          />
          <FloatingInput label={t("field_notes_label", "Notes (optional)")} value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="flex gap-3 pt-2">
            <Button variant="primary" onClick={handleSubmit} loading={loading} className="flex-1">{t("btn_close_shift", "Close Shift")}</Button>
            <Button variant="secondary" onClick={onClose} disabled={loading} className="flex-1">{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Shifts() {
  const { t } = useTranslation("shifts");
  const { t: tCommon } = useTranslation("common");

  const STATUS_OPTIONS = [
    { value: "", label: t("status_option_all", "All statuses") },
    { value: "open", label: t("status_option_open", "Open") },
    { value: "closed", label: t("status_option_closed", "Closed") },
  ];

  const [shifts, setShifts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "opened_at", direction: "desc" });
  const [closingShift, setClosingShift] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchShifts = async () => {
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getShifts(token, params);
      setShifts(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchShifts(); }, [branchFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  const sortedAndFiltered = useMemo(() => {
    let result = [...shifts];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [shifts, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-700 font-bold", render: (row) => row.branch?.name || `#${row.branch_id}` },
    { key: "cashier", label: t("col_cashier", "Cashier"), className: "text-gray-600", render: (row) => row.cashier?.username || `#${row.cashier_id}` },
    { key: "opening_balance", label: t("col_opening", "Opening"), className: "text-gray-700 font-mono", render: (row) => money(row.opening_balance) },
    {
      key: "closing_balance",
      label: t("col_closing_expected", "Closing (Expected)"),
      className: "text-gray-700 font-mono",
      render: (row) => row.status === "closed"
        ? `${money(row.closing_balance)} (${money(row.expected_closing_balance)})`
        : "—",
    },
    { key: "opened_at", label: t("col_opened_at", "Opened At"), className: "text-gray-500", render: (row) => new Date(row.opened_at).toLocaleString() },
    {
      key: "status",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.status === "open" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      {closingShift && (
        <CloseShiftModal
          shift={closingShift}
          onClose={() => setClosingShift(null)}
          onSaved={() => { setClosingShift(null); fetchShifts(); }}
        />
      )}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Shifts size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Shifts")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Cash register sessions — open one from the Checkout screen")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Checkout} onClick={() => navigate("/checkout")} className="w-full sm:w-auto shrink-0">
          {t("btn_go_to_checkout", "Go to Checkout")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <SelectBox label={t("filter_branch_label", "Filter by branch")} options={branchOptions} value={branchFilter} onChange={(val) => { setBranchFilter(val); setCurrentPage(1); }} />
        <SelectBox label={t("filter_status_label", "Filter by status")} options={STATUS_OPTIONS} value={statusFilter} onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          row.status === "open" ? (
            <Button variant="danger" size="sm" icon={ICONS.Shifts} onClick={() => setClosingShift(row)}>{isMobile && ` ${tCommon("btn_close", "Close")}`}</Button>
          ) : (
            <span className="text-gray-300 text-xs">—</span>
          )
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.branch?.name}</span>
            <h3 className="font-bold text-gray-800">{row.cashier?.username}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
